// ABOUTME: Listening Tour overview — stage-aware dashboard
// ABOUTME: Shows framework readiness when empty; fills in evidence + analysis as interviews arrive

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverview } from '@/hooks/useOverview';

const Tile: React.FC<{ label: string; value: number; muted?: boolean }> = ({ label, value, muted }) => (
  <div className={`rounded-xl border p-4 ${muted ? 'border-gray-200 bg-gray-50/60' : 'border-gray-200 bg-white'}`}>
    <div className={`text-2xl font-bold tabular-nums ${muted ? 'text-gray-400' : 'text-alx-blue'}`}>
      {value.toLocaleString()}
    </div>
    <div className="mt-0.5 text-xs uppercase tracking-wide text-gray-500">{label}</div>
  </div>
);

const Step: React.FC<{ n: number; title: string; body: string; done?: boolean }> = ({ n, title, body, done }) => (
  <li className="flex gap-3">
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        done ? 'bg-alx-green/20 text-alx-blue' : 'bg-alx-blue/5 text-alx-cyan'
      }`}
    >
      {done ? '✓' : n}
    </span>
    <div>
      <div className="text-sm font-medium text-gray-800">{title}</div>
      <div className="text-sm text-gray-500">{body}</div>
    </div>
  </li>
);

const OverviewPage: React.FC = () => {
  const { data, isLoading, error } = useOverview();
  const navigate = useNavigate();

  const getCategoryFromId = (hypothesisId: string): string => hypothesisId.split('_')[0] || 'unknown';

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-alx-blue/20 border-t-alx-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Couldn't load the overview. {error instanceof Error ? error.message : ''}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const overview = data.analysis_metadata?.dataset_overview;
  const evidenceMetrics = data.evidence_metrics;
  const hypothesisPerformance = data.hypothesis_performance;
  const researchAreas = data.research_area_metrics;

  const interviews = overview?.total_interviews || 0;
  const analysed = overview?.analyzed_hypotheses || 0;
  const evidence = overview?.total_evidence_points || 0;
  const hypotheses = overview?.total_hypotheses || 0;
  const researchAreaCount = overview?.research_areas || 0;
  const groups = overview?.persona_types || 0;

  const topValidated = hypothesisPerformance?.top_validated || [];
  const topRefuted = hypothesisPerformance?.top_refuted || [];
  const hasEvidence = (evidenceMetrics?.distribution?.length || 0) > 0;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <header className="mb-8 border-l-2 border-alx-green pl-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-alx-cyan">Listening Tour</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-alx-blue">Overview</h1>
        <p className="mt-2 max-w-2xl leading-relaxed text-gray-600">
          {interviews > 0 ? (
            <>
              Analysis across <strong className="text-alx-blue">{interviews}</strong> interviews and{' '}
              <strong className="text-alx-blue">{hypotheses}</strong> hypotheses, from{' '}
              <strong className="text-alx-blue">{evidence.toLocaleString()}</strong> evidence points.
            </>
          ) : (
            <>The hypothesis framework is loaded. Evidence and analysis fill in here as interviews are ingested.</>
          )}
        </p>
      </header>

      {/* Readiness */}
      <section className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Tile label="Hypotheses" value={hypotheses} />
        <Tile label="Research areas" value={researchAreaCount} />
        <Tile label="Groups" value={groups} />
        <Tile label="Interviews" value={interviews} muted={interviews === 0} />
        <Tile label="Evidence" value={evidence} muted={evidence === 0} />
        <Tile label="Analysed" value={analysed} muted={analysed === 0} />
      </section>

      {/* Empty-state: the pipeline ahead */}
      {interviews === 0 && (
        <section className="mb-8 rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-alx-blue">Ready for interviews</h2>
          <p className="mt-1 text-sm text-gray-600">
            The framework — {hypotheses} hypotheses with their signals — is loaded. As interviews come in,
            this overview populates:
          </p>
          <ol className="mt-4 space-y-3">
            <Step n={1} done title="Framework loaded" body="Hypotheses, themes and the signals to look for are in place." />
            <Step n={2} title="Ingest interviews" body="Each transcript is structured into questions and answers." />
            <Step n={3} title="Extract evidence" body="Answers are matched against each hypothesis's signals." />
            <Step n={4} title="Analyse hypotheses" body="Accumulated evidence is synthesised into a verdict per hypothesis." />
          </ol>
          <button
            onClick={() => navigate('/dashboard/browse')}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-alx-blue px-4 py-2 text-sm font-medium text-white transition hover:bg-alx-blue/90"
          >
            Browse the {hypotheses} hypotheses →
          </button>
        </section>
      )}

      {/* AI disclaimer — only meaningful once analysis exists */}
      {analysed > 0 && (
        <section className="mb-8 rounded-xl border-l-4 border-alx-cyan bg-alx-cyan/5 p-5">
          <h3 className="font-semibold text-alx-blue">AI-generated analysis</h3>
          <p className="mt-1 text-sm leading-relaxed text-gray-600">
            Evidence extraction and hypothesis analysis are AI-generated from interview transcripts. Treat
            them as a starting point, verify before acting, and flag any issues to the research team.
          </p>
        </section>
      )}

      {/* Evidence distribution */}
      {hasEvidence && (
        <section className="mb-8">
          <h2 className="mb-4 text-xl font-semibold text-alx-blue">Evidence</h2>
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
              {evidenceMetrics!.distribution!.map((item: any, index: number) => (
                <div key={index} className="text-center">
                  <div
                    className={`text-3xl font-bold tabular-nums ${
                      item.type === 'supports'
                        ? 'text-green-600'
                        : item.type === 'refutes'
                          ? 'text-red-600'
                          : item.type === 'mixed'
                            ? 'text-yellow-600'
                            : 'text-gray-500'
                    }`}
                  >
                    {item.percentage}%
                  </div>
                  <div className="mt-1 text-sm font-medium capitalize text-gray-700">{item.type}</div>
                  <div className="text-xs text-gray-500">{item.count.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-500">
              {evidenceMetrics?.total_evidence?.toLocaleString() || 0} evidence points analysed
            </div>
          </div>
        </section>
      )}

      {/* Hypothesis performance */}
      {(topValidated.length > 0 || topRefuted.length > 0) && (
        <section className="mb-8">
          <h2 className="mb-4 text-xl font-semibold text-alx-blue">Hypothesis performance</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {[
              { title: 'Strongest support', items: topValidated, border: 'border-green-500', badge: 'bg-green-100 text-green-800' },
              { title: 'Weakest support', items: topRefuted, border: 'border-red-500', badge: 'bg-red-100 text-red-800' },
            ].map((col) => (
              <div key={col.title}>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">{col.title}</h3>
                <div className="space-y-3">
                  {col.items.slice(0, 5).map((h: any, i: number) => (
                    <div
                      key={i}
                      onClick={() => navigate(`/dashboard/hypothesis/${getCategoryFromId(h.hypothesis_id)}/${h.hypothesis_id}`)}
                      className={`cursor-pointer rounded-lg border-l-4 ${col.border} bg-white p-4 shadow-sm transition hover:shadow-md`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs text-gray-400">{h.hypothesis_id}</div>
                          <h4 className="text-sm font-medium text-gray-800">{h.hypothesis_name}</h4>
                        </div>
                        <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-bold tabular-nums ${col.badge}`}>
                          {h.support_rate}%
                        </span>
                      </div>
                      <div className="mt-2 flex gap-3 text-xs text-gray-500">
                        <span className="text-green-600">{h.supporting} support</span>
                        <span className="text-red-600">{h.refuting} refute</span>
                        <span className="text-yellow-600">{h.mixed} mixed</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Research area performance */}
      {researchAreas && researchAreas.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-4 text-xl font-semibold text-alx-blue">Research areas</h2>
          <div className="space-y-4">
            {researchAreas.map((area: any, index: number) => (
              <div key={index} className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-alx-blue">{area.area_name}</h3>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-medium ${
                      area.avg_support_rate >= 80
                        ? 'bg-green-600 text-white'
                        : area.avg_support_rate >= 50
                          ? 'bg-yellow-500 text-white'
                          : 'bg-red-600 text-white'
                    }`}
                  >
                    {area.avg_support_rate}% avg support
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3 text-sm">
                    <span className="text-gray-600">Hypotheses</span>
                    <span className="font-medium tabular-nums text-alx-blue">{area.hypothesis_count}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3 text-sm">
                    <span className="text-gray-600">Evidence</span>
                    <span className="font-medium tabular-nums text-alx-blue">{area.evidence_count}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3 text-sm">
                    <span className="text-gray-600">Support rate</span>
                    <span className="font-medium tabular-nums text-alx-blue">{area.avg_support_rate}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default OverviewPage;
